<script setup lang="ts">
/**
 * Files section in the matter-context modal's left pane. Renders:
 *
 *   - "Documents in this matter": read-only list of files already uploaded +
 *     extracted (store.existingDocuments, hydrated on open). No remove control.
 *   - "Analyzed (N)": files sent through an extraction this session
 *     (`processed`), rendered as plain contributed documents — normal type
 *     icon, no status note, no remove control.
 *   - the staging area: helper text + accepted-formats line, then the list of
 *     files staged this session but not yet analyzed (type icons +
 *     duplicate state + X).
 *
 * Drag-and-drop is handled at the modal level (see MatterContextModal.vue);
 * this component only owns the file picker and the rendered list.
 *
 * Files that fail client-side validation (extension, size, empty)
 * are still added to the list so the user can see and remove them,
 * but they render in an error state and the store's `canRunExtraction`
 * gate refuses to fire while any are present.
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import {
    MATTER_CONTEXT_ACCEPT_ATTR,
    describeMatterContextFileError,
    formatAllowedExtensionsLabel,
} from '../../utils/matterContextFiles';

const { t } = useI18n();
const store = useMatterContextModalStore();

const acceptedFormatsLabel = formatAllowedExtensionsLabel();

// Files sent through an extraction this session, grouped under the
// "Analyzed (N)" header; everything else stays in the staging list.
const processedFiles = computed(() => store.stagedFiles.filter((f) => f.processed));
const pendingFiles = computed(() => store.stagedFiles.filter((f) => !f.processed));

const fileInputRef = ref<HTMLInputElement | null>(null);

const inputDisabled = computed(() => !store.inputsEditable);

const onPickFiles = () => {
    if (inputDisabled.value) return;
    fileInputRef.value?.click();
};

const onFileInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files) {
        store.stageFiles(Array.from(target.files));
        target.value = '';
    }
};

const fileTypeIcon = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'doc' || ext === 'docx') return 'article';
    if (ext === 'xls' || ext === 'xlsx') return 'table_chart';
    if (ext === 'csv') return 'table_chart';
    if (ext === 'pdf') return 'picture_as_pdf';
    if (ext === 'md' || ext === 'txt') return 'text_snippet';
    return 'insert_drive_file';
};

const fileTypeClass = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'doc' || ext === 'docx') return 'mc-file-icon-word';
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return 'mc-file-icon-excel';
    if (ext === 'pdf') return 'mc-file-icon-pdf';
    return 'mc-file-icon-text';
};

const fileTypeFriendlyName = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'doc' || ext === 'docx') return t('matterContext.files.fileType.word');
    if (ext === 'xls' || ext === 'xlsx') return t('matterContext.files.fileType.excel');
    if (ext === 'csv') return t('matterContext.files.fileType.csv');
    if (ext === 'pdf') return t('matterContext.files.fileType.pdf');
    if (ext === 'md') return t('matterContext.files.fileType.markdown');
    if (ext === 'txt') return t('matterContext.files.fileType.text');
    return t('matterContext.files.fileType.document');
};

const formatFileSize = (bytes: number) => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
};
</script>

<template>
    <section class="mc-section mc-files-section">
        <header class="mc-section-header">
            <h3 class="mc-section-title">{{ t('matterContext.files.title') }}</h3>
            <button
                type="button"
                class="mc-section-action"
                :aria-label="t('matterContext.files.attachFiles')"
                :disabled="inputDisabled"
                @click="onPickFiles"
            >
                <span class="material-icons" style="font-size: 18px">add</span>
            </button>
        </header>
        <p class="mc-section-helper">{{ t('matterContext.files.helper') }}</p>
        <p
            v-if="store.stagedFiles.length === 0 && store.existingDocuments.length === 0"
            class="mc-files-formats"
        >
            {{ t('matterContext.files.acceptedFormats', { formats: acceptedFormatsLabel }) }}
        </p>

        <input
            ref="fileInputRef"
            type="file"
            multiple
            :accept="MATTER_CONTEXT_ACCEPT_ATTR"
            style="display: none"
            @change="onFileInputChange"
        />

        <!-- Documents already uploaded + extracted for this matter (read-only). -->
        <div v-if="store.existingDocuments.length > 0" class="mc-existing-docs">
            <p class="mc-existing-docs-label">{{ t('matterContext.files.documentsInMatter') }}</p>
            <ul class="mc-files-list">
                <li
                    v-for="d in store.existingDocuments"
                    :key="d.hash || d.fileName"
                    class="mc-files-item mc-files-item-existing"
                >
                    <span
                        class="material-icons mc-file-icon"
                        :class="fileTypeClass(d.fileName)"
                        :title="fileTypeFriendlyName(d.fileName)"
                        :aria-label="fileTypeFriendlyName(d.fileName)"
                        >{{ fileTypeIcon(d.fileName) }}</span
                    >
                    <span class="mc-file-name">{{ d.fileName }}</span>
                    <span v-if="formatFileSize(d.fileSizeBytes)" class="mc-file-size">{{
                        formatFileSize(d.fileSizeBytes)
                    }}</span>
                </li>
            </ul>
        </div>

        <!-- Files analyzed this session — plain rows under a count header. -->
        <div v-if="processedFiles.length > 0" class="mc-existing-docs">
            <p class="mc-existing-docs-label">{{ t('matterContext.files.analyzed', { count: processedFiles.length }) }}</p>
            <ul class="mc-files-list">
                <li v-for="f in processedFiles" :key="f.file.name" class="mc-files-item">
                    <span
                        class="material-icons mc-file-icon"
                        :class="fileTypeClass(f.file.name)"
                        :title="fileTypeFriendlyName(f.file.name)"
                        :aria-label="fileTypeFriendlyName(f.file.name)"
                        >{{ fileTypeIcon(f.file.name) }}</span
                    >
                    <span class="mc-file-name">{{ f.file.name }}</span>
                </li>
            </ul>
        </div>

        <ul v-if="pendingFiles.length > 0" class="mc-files-list">
            <li
                v-for="f in pendingFiles"
                :key="f.file.name"
                class="mc-files-item"
                :class="{
                    'mc-files-item-invalid': !f.valid,
                    'mc-files-item-duplicate': f.valid && f.duplicate,
                }"
            >
                <span
                    v-if="!f.valid"
                    class="material-icons mc-file-icon mc-file-icon-invalid"
                    :title="f.reason ? describeMatterContextFileError(f.reason) : t('matterContext.files.invalidFile')"
                    :aria-label="
                        f.reason ? describeMatterContextFileError(f.reason) : t('matterContext.files.invalidFile')
                    "
                    >error</span
                >
                <span
                    v-else-if="f.duplicate"
                    class="material-icons mc-file-icon"
                    style="color: #9a6a00"
                    :title="t('matterContext.files.duplicateNote')"
                    :aria-label="t('matterContext.files.duplicateNote')"
                    >content_copy</span
                >
                <span
                    v-else
                    class="material-icons mc-file-icon"
                    :class="fileTypeClass(f.file.name)"
                    :title="fileTypeFriendlyName(f.file.name)"
                    :aria-label="fileTypeFriendlyName(f.file.name)"
                    >{{ fileTypeIcon(f.file.name) }}</span
                >
                <span class="mc-file-name">{{ f.file.name }}</span>
                <span
                    v-if="f.valid && f.duplicate"
                    class="mc-file-dup-note"
                    style="font-size: 11px; color: #9a6a00; margin-left: 6px; white-space: nowrap"
                    >{{ t('matterContext.files.duplicateNote') }}</span
                >
                <button
                    type="button"
                    class="mc-file-remove"
                    :aria-label="t('matterContext.files.removeFile')"
                    :disabled="inputDisabled"
                    @click="store.removeStagedFile(f.file.name)"
                >
                    <span class="material-icons" style="font-size: 16px">cancel</span>
                </button>
            </li>
        </ul>
    </section>
</template>
